/**
 * Small per-user drafts kept in localStorage, so a screen can come back after
 * Android closes the app in the background (B-59, B-39).
 *
 * Phones close background apps all the time: behind the photo picker, behind
 * the camera, or just because another app needed the memory. The WebView then
 * starts again at "/" with nothing in memory, so the trade the collector had
 * open, a half-typed chat reply, a half-built offer and a half-filled listing
 * were all lost. Each of those screens now writes what the user has typed here
 * as it changes, and reads it back when it opens again.
 *
 * The rules every caller relies on:
 *   - A draft belongs to ONE account. It is stored with the user id and read
 *     back only for that id; another account's draft is deleted, not shown.
 *   - A draft is short lived. Older than DRAFT_TTL_MS it is deleted on read.
 *     Its age counts from the last write or the last time the app was left
 *     (see onLeave), whichever is later.
 *   - Only what the user typed or picked is stored (ids, text, a grade), never
 *     a token or anything the server sent that the screen can fetch again.
 *   - Writes are debounced (saveDraftSoon), so typing does not write on every
 *     key, and anything still waiting is written as soon as the page is hidden,
 *     which is the last moment before Android may close the app.
 *   - An empty value removes the draft at once, which is how a sent message or
 *     a closed sheet stops coming back.
 *   - Storage can be missing or throw (private mode, blocked site data), so
 *     every access is guarded and a failure just means nothing is restored.
 */

const PREFIX = "tl.draft.";

/** How long a draft is worth restoring. */
export const DRAFT_TTL_MS = 30 * 60 * 1000;

/** How long saveDraftSoon waits for typing to pause. */
export const DRAFT_DEBOUNCE_MS = 400;

const pending = {};
let timer;

/** Store `value` for `userId` now. No user or an empty value removes it. */
export const writeDraft = (name, userId, value) => {
  delete pending[name];
  try {
    if (!userId || value == null || value === "") localStorage.removeItem(PREFIX + name);
    else localStorage.setItem(PREFIX + name, JSON.stringify({ u: String(userId), t: Date.now(), v: value }));
  } catch {
    // Storage unavailable: the draft is simply not kept.
  }
};

/** The draft `userId` left under `name`, or null if none is fresh and theirs. */
export const readDraft = (name, userId) => {
  // A write still waiting on the debounce is the newest value: land it first,
  // or reopening a conversation right after leaving it reads an older reply.
  if (pending[name]) pending[name]();
  try {
    const d = JSON.parse(localStorage.getItem(PREFIX + name));
    if (!d) return null;
    if (userId && d.u === String(userId) && Date.now() - d.t < DRAFT_TTL_MS) return d.v;
    localStorage.removeItem(PREFIX + name);
  } catch {
    // Unreadable or unparsable: nothing to restore.
  }
  return null;
};

/** Write every draft still waiting on the debounce. */
export const flushDrafts = () => {
  clearTimeout(timer);
  Object.keys(pending).forEach((name) => pending[name]());
};

/** Debounced writeDraft. An empty value is written (removed) at once. */
export const saveDraftSoon = (name, userId, value) => {
  if (!userId || value == null || value === "") return writeDraft(name);
  pending[name] = () => writeDraft(name, userId, value);
  clearTimeout(timer);
  timer = setTimeout(flushDrafts, DRAFT_DEBOUNCE_MS);
};

/** Call `fn(key, draft)` for every stored draft; `draft` is null once expired. */
const eachDraft = (fn) => {
  try {
    for (let i = localStorage.length; i--; ) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PREFIX)) continue;
      let d = null;
      try {
        d = JSON.parse(localStorage.getItem(key));
      } catch {
        // Unparsable: treated as expired.
      }
      fn(key, d && Date.now() - d.t < DRAFT_TTL_MS ? d : null);
    }
  } catch {
    // Storage unavailable: nothing was kept.
  }
};

/**
 * Remove drafts: every one on sign out, or only the expired ones (`staleOnly`),
 * which is how drafts nobody came back for stop piling up.
 */
export const clearDrafts = (staleOnly) => {
  if (!staleOnly) Object.keys(pending).forEach((name) => delete pending[name]);
  eachDraft((key, fresh) => {
    if (!staleOnly || !fresh) localStorage.removeItem(key);
  });
};

/**
 * The app is leaving the screen: home, the app switcher, or the photo picker or
 * camera opening over it. It is the last safe moment before Android may close
 * the app, so what is still waiting is written now. And "recent" means recently
 * USED, not recently typed: a trade page read for forty minutes before the user
 * switched away is still the screen they were on, so every live draft is
 * stamped with this moment. An expired one is left expired.
 */
const onLeave = () => {
  flushDrafts();
  eachDraft((key, fresh) => {
    if (fresh) localStorage.setItem(key, JSON.stringify({ ...fresh, t: Date.now() }));
  });
};

// Both signals, because either alone can be missed: the page being hidden, and
// the "pause" Capacitor's Android bridge fires on the document when the
// activity pauses (which includes the photo picker or camera opening).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) onLeave();
  });
  document.addEventListener("pause", onLeave);
}
